import { useTheme } from '@fluentui/react';
import { useId } from '@fluentui/react-hooks';
import { IPCallout, IPFontIcon, IPLink, IPText, IPTooltipHost } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { acceptIcon, aiBackgroundIcon, cancelIcon, commentIcon, globalStackTokensGapSmall } from 'globalStyles';
import AICriterionOutcomeDetails from 'components/AuditCriteria/AICriterionOutcomeDetails';
import {
  AICriterionOutcomeResult,
  AuditCriterionValueAIStatus,
  AuditCriterionValueSource,
} from 'models/auditCriteriaGeneric';
import { AuditCriteriaValue, AuditCriteriaValueOutcome } from 'models/auditCriteriaValues';
import { MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { getOutcomeSourceDisplayName } from '../automationNames';
import { toLocaleDateShort } from 'utils/datetime';

interface ICriteriaValueCellProps {
  value: AuditCriteriaValue | undefined;

  // the latest AI outcome still proposed or applied, whatever the period
  outcome?: AuditCriteriaValueOutcome;
}

// PLAN-075 (spec 9.1): one criteria cell of a list: pass, fail or the value with the date of the answer; the tooltip
// names the comment, the evidence, the source, the AI status and the latest AI outcome; the date opens the task of the
// answer, the AI icon the details of the outcome.
const CriteriaValueCell = (props: ICriteriaValueCellProps) => {
  const { t } = useTranslation(['criteria']);
  const theme = useTheme();
  const value = props.value;
  const outcome = props.outcome;
  const outcomeLinkId = useId('criteria-cell-outcome');
  const [showOutcome, setShowOutcome] = useState<boolean>(false);

  const getOutcomeText = (): string | undefined => {
    if (!outcome) return undefined;

    const result =
      outcome.proposedResult === AICriterionOutcomeResult.Pass
        ? t('criteria:Generic.Outcome.Pass')
        : outcome.proposedResult === AICriterionOutcomeResult.Fail
        ? t('criteria:Generic.Outcome.Fail')
        : t('criteria:Generic.Outcome.Inconclusive');

    return t('criteria:Generic.Outcome.Available', {
      result,
      source:
        getOutcomeSourceDisplayName(
          outcome.sourceKind,
          outcome.sourceRef,
          outcome.sourceName,
          t as TFunction<string[]>,
        ) || t('criteria:Generic.Outcome.SourceRemoved'),
      date: toLocaleDateShort(outcome.created),
    });
  };

  const outcomeText = getOutcomeText();

  // the row takes no click of its own: the link opens the details only
  const toggleOutcome = (ev: MouseEvent) => {
    ev.stopPropagation();
    setShowOutcome(!showOutcome);
  };

  const outcomeLink = outcome ? (
    <IPLink id={outcomeLinkId} onClick={toggleOutcome} title={t('criteria:Generic.Outcome.OpenResult')}>
      <IPFontIcon iconName={aiBackgroundIcon.iconName} />
    </IPLink>
  ) : null;

  const outcomeCallout =
    outcome && showOutcome ? (
      <IPCallout
        target={`#${outcomeLinkId}`}
        onDismiss={() => setShowOutcome(false)}
        styles={{ calloutMain: { padding: 12, maxHeight: '50vh', overflowY: 'auto' } }}
      >
        <AICriterionOutcomeDetails outcomeId={outcome.outcomeId} />
      </IPCallout>
    ) : null;

  if (!value) {
    if (!outcome) return <IPText variant="small">{t('criteria:Columns.NotAssessed')}</IPText>;

    return (
      <IPTooltipHost content={outcomeText}>
        <IPStack horizontal verticalAlign="center" tokens={globalStackTokensGapSmall}>
          <IPText variant="small">{t('criteria:Columns.NotAssessed')}</IPText>
          {outcomeLink}
        </IPStack>
        {outcomeCallout}
      </IPTooltipHost>
    );
  }

  const getResultText = (): string => {
    if (value.isPass()) return t('criteria:Columns.Pass');
    if (value.isFail()) return t('criteria:Columns.Fail');

    return value.resultNumber !== undefined ? value.resultNumber.toString() : t('criteria:Columns.NoValue');
  };

  const getSourceText = (): string | undefined => {
    switch (value.source) {
      case AuditCriterionValueSource.Assessment:
        return t('criteria:Columns.Source.Assessment');
      case AuditCriterionValueSource.Audit:
        return t('criteria:Columns.Source.Audit');
      case AuditCriterionValueSource.Integration:
        return t('criteria:Columns.Source.Integration');
      default:
        return undefined;
    }
  };

  const getAIStatusText = (): string | undefined => {
    switch (value.aiStatus) {
      case AuditCriterionValueAIStatus.Satisfied:
        return t('criteria:EvidenceStatus.Satisfied');
      case AuditCriterionValueAIStatus.PartiallySatisfied:
        return t('criteria:EvidenceStatus.PartiallySatisfied');
      case AuditCriterionValueAIStatus.NotSatisfied:
        return t('criteria:EvidenceStatus.NotSatisfied');
      case AuditCriterionValueAIStatus.Contradicted:
        return t('criteria:EvidenceStatus.Contradicted');
      case AuditCriterionValueAIStatus.NotApplicable:
        return t('criteria:EvidenceStatus.NotApplicable');
      case AuditCriterionValueAIStatus.NotInspectable:
        return t('criteria:EvidenceStatus.NotInspectable');
      case AuditCriterionValueAIStatus.Unknown:
        return t('criteria:EvidenceStatus.Unknown');
      case AuditCriterionValueAIStatus.Proposed:
        return t('criteria:Columns.AIProposed');
      case AuditCriterionValueAIStatus.Accepted:
        return t('criteria:Columns.AIAccepted');
      case AuditCriterionValueAIStatus.NotAuditedManual:
        return t('criteria:NotAudited.Manual');
      case AuditCriterionValueAIStatus.NotAuditedNoEvidence:
        return t('criteria:NotAudited.NoEvidence');
      case AuditCriterionValueAIStatus.NotAuditedNotInspected:
        return t('criteria:NotAudited.NotInspected');
      case AuditCriterionValueAIStatus.NotAuditedEvidenceTaskOpen:
        return t('criteria:NotAudited.EvidenceTaskOpen');
      case AuditCriterionValueAIStatus.NotAuditedNoCheckRuns:
        return t('criteria:NotAudited.NoCheckRuns');
      default:
        return undefined;
    }
  };

  const resultText = getResultText();
  const sourceText = getSourceText();
  const aiStatusText = getAIStatusText();
  const dateText = toLocaleDateShort(value.resultDate);

  const tooltip = (
    <IPStack tokens={globalStackTokensGapSmall}>
      <IPText>{t('criteria:Columns.AnsweredOn', { result: resultText, date: dateText })}</IPText>
      {sourceText && <IPText>{sourceText}</IPText>}
      <IPText>{value.hasComment ? t('criteria:Columns.HasComment') : t('criteria:Columns.NoComment')}</IPText>
      <IPText>{value.hasEvidence ? t('criteria:Columns.HasEvidence') : t('criteria:Columns.NoEvidence')}</IPText>
      {aiStatusText && <IPText>{t('criteria:Columns.AIStatus', { status: aiStatusText })}</IPText>}
      {outcomeText && <IPText>{outcomeText}</IPText>}
    </IPStack>
  );

  const resultIcon = value.isPass() ? (
    <IPFontIcon iconName={acceptIcon.iconName} style={{ color: theme.semanticColors.successIcon }} />
  ) : value.isFail() ? (
    <IPFontIcon iconName={cancelIcon.iconName} style={{ color: theme.semanticColors.errorIcon }} />
  ) : (
    <IPText>{resultText}</IPText>
  );

  return (
    <IPTooltipHost content={tooltip}>
      <IPStack horizontal verticalAlign="center" tokens={globalStackTokensGapSmall}>
        {resultIcon}
        {value.taskId ? (
          <IPLink
            href={`/tasks/${value.taskId}`}
            target="_blank"
            rel="noopener noreferrer"
            title={t('criteria:Columns.OpenTask')}
          >
            {dateText}
          </IPLink>
        ) : (
          <IPText>{dateText}</IPText>
        )}
        {value.hasComment && <IPFontIcon iconName={commentIcon.iconName} />}
        {outcomeLink ?? (aiStatusText && <IPFontIcon iconName={aiBackgroundIcon.iconName} />)}
      </IPStack>
      {outcomeCallout}
    </IPTooltipHost>
  );
};

export default CriteriaValueCell;
